import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Calendar, Users, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { EventOccurrenceWithBookings } from '@/types/events';
import { useTranslate } from '@/i18n';

interface OccurrenceSelectorProps {
  occurrences: EventOccurrenceWithBookings[];
  selectedOccurrenceId: string | null;
  onSelect: (occurrenceId: string) => void;
  maxSpots: number;
  disabled?: boolean;
}

export function OccurrenceSelector({
  occurrences,
  selectedOccurrenceId,
  onSelect,
  maxSpots,
  disabled = false,
}: OccurrenceSelectorProps) {
  const t = useTranslate();
  if (occurrences.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
        <AlertCircle className="h-8 w-8 mb-2" />
        <p className="text-sm">Inga kommande tillfällen</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium mb-3">Välj tillfälle</p>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {occurrences.map((occ) => {
          const spotsLeft = (occ.capacity ?? maxSpots) - occ.booked_spots;
          const isFullyBooked = spotsLeft <= 0;
          const isSelected = occ.id === selectedOccurrenceId;
          
          return (
            <Button
              key={occ.id}
              type="button"
              variant={isSelected ? "default" : "outline"}
              className={cn(
                "w-full justify-start h-auto py-3 px-4",
                isFullyBooked && "opacity-50 cursor-not-allowed"
              )}
              disabled={disabled || isFullyBooked}
              onClick={() => onSelect(occ.id)}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 shrink-0" />
                  <div className="text-left">
                    <p className="font-medium">
                      {format(new Date(occ.start_at), "EEEE d MMMM", { locale: sv })}
                    </p>
                    <p className="text-xs opacity-80">
                      {format(new Date(occ.start_at), "HH:mm")} - {format(new Date(occ.end_at), "HH:mm")}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {isFullyBooked ? (
                    <Badge variant="secondary" className="text-xs">
                      {t("status.fullyBooked")}
                    </Badge>
                  ) : (
                    <span className="text-xs flex items-center gap-1 opacity-80">
                      <Users className="h-3 w-3" />
                      {spotsLeft} kvar
                    </span>
                  )}
                </div>
              </div>
            </Button>
          );
        })}
      </div>
    </div>
  );
}
